import axios from 'axios';
import { apiUrl } from '../Config';

const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken');
};

/**
 * Fetch job applications for the logged-in employer.
 * Maps the response so each item exposes candidate details at the top level
 * for easy consumption by the Candidates page.
 *
 * Supported filters: { search, title, location, status, page, limit }
 */
export const searchCandidates = async (filters = {}) => {
    try {
        const token = getAuthToken();
        const params = {};
        
        if (typeof filters === 'string') {
            const searchParams = new URLSearchParams(filters);
            for (const [key, value] of searchParams.entries()) {
                params[key] = value;
            }
            if (params.jobTitle) {
                if (!params.title) params.title = params.jobTitle;
            }
            if (params.search && !params.name) {
                params.name = params.search;
            }
        } else {
            if (filters.search) {
                params.search = filters.search;
                // `search` is supported by older API versions; `name` is the
                // explicit candidate-name filter used by the current API.
                params.name = filters.search;
            }
            if (filters.title) {
                params.title = filters.title;
            }
            if (filters.jobTitle) {
                params.title = filters.jobTitle;
            }
            if (filters.location) params.location = filters.location;
            if (filters.status)   params.status   = filters.status;
            if (filters.page)     params.page     = filters.page;
            if (filters.limit)    params.limit    = filters.limit;
            if (filters.skills)   params.skills   = filters.skills;
        }

        const response = await axios.get(`${apiUrl}/jobapplication/job-applications-by-user`, {
            headers: { Authorization: `Bearer ${token}` },
            params,
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching candidates:', error);
        throw error;
    }
};

export const searchAllCandidates = async (filters = {}) => {
    try {
        const token = getAuthToken();
        const params = {};
        
        if (typeof filters === 'string') {
            const searchParams = new URLSearchParams(filters);
            for (const [key, value] of searchParams.entries()) {
                if (key === 'jobTitle') {
                    params.jobTitle = value;
                    params.title = value;
                } else if (key === 'title') {
                    params.title = value;
                } else if (key === 'search' || key === 'query') {
                    params.search = value;
                } else {
                    params[key] = value;
                }
            }
        } else {
            if (filters.search) {
                params.search = filters.search;
            }
            if (filters.title) {
                params.title = filters.title;
                if (!params.jobTitle) params.jobTitle = filters.title;
            }
            if (filters.jobTitle) {
                params.jobTitle = filters.jobTitle;
                params.title = filters.jobTitle;
            }
            if (filters.location) params.location = filters.location;
            if (filters.page)     params.page     = filters.page;
            if (filters.limit)    params.limit    = filters.limit;
            if (filters.skills)   params.skills   = filters.skills;
            if (filters.experience) params.experience = filters.experience;
            if (filters.noticePeriod) params.noticePeriod = filters.noticePeriod;
            if (filters.education) params.education = filters.education;
        }

        const response = await axios.get(`${apiUrl}/users/search-users`, {
            headers: { Authorization: `Bearer ${token}` },
            params,
        });
        return response.data;
    } catch (error) {
        console.error('Error searching all candidates:', error);
        throw error;
    }
};

export const getCandidateById = async (userId) => {
    try {
        const token = getAuthToken();
        const response = await axios.get(`${apiUrl}/users/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching candidate by ID:', error);
        throw error;
    }
};

export const getCandidateResume = async (userId, jobId = null) => {
    try {
        const token = getAuthToken();
        const query = jobId ? `?jobId=${jobId}` : '';
        const response = await axios.get(`${apiUrl}/user/resume/getby/${userId}${query}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching candidate resume:', error);
        throw error;
    }
};

export const downloadCandidateResume = async (userId) => {
    const token = getAuthToken();
    const url = `${apiUrl}/user/resume/download/${userId}`;
    // Open in new tab to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');
    link.setAttribute('target', '_blank');
    // Add token as query param for download
    link.href = `${url}?token=${token}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const createInterview = async (interviewData) => {
    try {
        const token = getAuthToken();
        // Backend requires: jobId, applicationId, candidateId, interviewStartAt, 
        // createdByEmployerId, status, mode, timeZone, durationMinutes, meetingLink, locationText, message
        const response = await axios.post(`${apiUrl}/interviews`, interviewData, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating interview:', error);
        throw error;
    }
};

export const getCandidateIntroVideo = async (userId) => {
    try {
        const token = getAuthToken();
        const response = await axios.get(`${apiUrl}/user/video/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching intro video:', error);
        throw error;
    }
};

export const updateCandidateStatus = async (applicationId, status) => {
    try {
        const token = getAuthToken();
        const response = await axios.post(`${apiUrl}/jobapplication/update-jobapplication-status`, {
            applicationId,
            status
        }, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating candidate status:', error);
        throw error;
    }
};
